// =============================================================================
// FORMULAIRE DAL - Diagnostic Autonomie Logement
// Structure alignée sur le document officiel TEF
// =============================================================================
import { useState, useEffect, useCallback, useRef } from "react";
import {
  FileText, Home, User, Heart, Camera, Download, Save,
  ChevronDown, ChevronRight, CheckCircle2, AlertCircle,
  Plus, X, Upload, Grid, Wand2, FileDown, Loader2,
  ShieldCheck, Scale, Target, Zap, AlertTriangle, Printer,
  Building, Calendar, Phone, Mail, MapPin, Sparkles, PencilRuler, Info
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import AgentMarkdown from "../agents/AgentMarkdown";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { OhCard, OhStatusNote } from "../../components/ui/oh-charte";
// BLOC 4 — le plan validable. Composant AUTONOME : ce fichier fait 1700+ lignes
// et sert en exploitation, on ne l'ouvre qu'à un seul endroit.
import PlanDalPanel from "./PlanDalPanel";
import { toast } from "sonner";
import Plan2DGenerator from "../dal/Plan2DGenerator";
import AIImageAfter from "../dal/AIImageAfter";
import { DALAgentPanel } from "./DALAgentPanel";

// ═══ Styles charte OmniHub — hiérarchie boutons + surfaces, dual-mode via tokens
// (cf. docs/upload/CHARTE-GRAPHIQUE-OMNIHUB.md §2/§4). Un seul accent par écran :
// bouton PRIMAIRE = fond accent plein ; SECONDAIRE = contour accent ; DISCRET =
// variant outline neutre (shadcn). On réutilise le gabarit validé du DAL Mobile.
const btnPrimary = { background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" };
const btnSecondary = { background: "transparent", border: "1px solid var(--oh-accent)", color: "var(--oh-accent-text)" };
const surfaceCard = { background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)" };
const neutralBadgeStyle = { background: "var(--oh-bg-surface)", color: "var(--oh-text-secondary)", border: "1px solid var(--oh-border)" };
import { useConfigList } from "../../hooks/useConfigList";
import { buildSolutionsMappingFromZones, mapLookupVisiteursResponse } from "../dal/dalState";
import { formatClientAddress, formatClientFullName } from "../../utils/clientFormat";


// Import DAL PDF — Phase 1 (JJ 2026-05-20)
// Bouton "Importer DAL existant (PDF)" + preview iframe si déjà importé.
// Backend : POST /api/metier/tef/dossiers/{id}/dal/import-pdf (multipart/form-data)
// Stocke dans workflow_data.tef.infos_dal.dal_imported_pdf + Zoho 02_PIECES_DU_DOSSIER.
function ImportDALPDFBlock({ dossierId, tenantId, dalImportedPdf, onImported }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Sélectionnez un fichier PDF");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem("session_token");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("nom", `DAL importé — ${file.name}`);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/metier/tef/dossiers/${dossierId}/dal/import-pdf`,
        { credentials: "include",
          method: "POST",
          headers: {
            "X-Tenant-ID": tenantId || "tef",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          credentials: "include",
          body: fd,
        }
      );
      if (res.ok) {
        const data = await res.json();
        toast.success(`DAL PDF importé (Zoho ${data.dal_imported_pdf?.zoho_subfolder || "02_PIECES"})`);
        onImported?.(data.dal_imported_pdf);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Erreur import (HTTP ${res.status})`);
      }
    } catch (err) {
      toast.error(`Erreur réseau : ${err.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="mb-4 p-4 rounded-lg border" style={surfaceCard}
         data-testid="dal-import-pdf-block">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4" style={{ color: "var(--oh-accent-text)" }} />
          <span className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>
            DAL existant ? Importez le PDF
          </span>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleUpload}
            className="hidden"
            data-testid="dal-import-pdf-file-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            size="sm"
            variant="outline"
            style={btnSecondary}
            data-testid="dal-import-pdf-button"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            {dalImportedPdf ? "Remplacer le PDF" : "Importer un DAL existant (PDF)"}
          </Button>
        </div>
      </div>
      {!dalImportedPdf && (
        <p className="text-xs mt-2" style={{ color: "var(--oh-text-muted)" }}>
          Upload → Zoho 02_PIECES_DU_DOSSIER. Copie auto vers 03_DEPOT
          dès que contrat et mandat sont signés.
        </p>
      )}
      {dalImportedPdf && (
        <div className="mt-3 space-y-2">
          <div className="text-xs flex flex-wrap items-center gap-2" style={{ color: "var(--oh-text-secondary)" }}>
            <CheckCircle2 className="w-3 h-3" style={{ color: "var(--oh-success-text)" }} />
            <span>{dalImportedPdf.nom || dalImportedPdf.filename}</span>
            <span className="text-[var(--oh-text-muted)]">
              ({Math.round((dalImportedPdf.size || 0) / 1024)} KB, importé
              {dalImportedPdf.uploaded_at ? ` le ${dalImportedPdf.uploaded_at.slice(0, 10)}` : ""})
            </span>
          </div>
          {dalImportedPdf.document_id && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/metier/documents/${dalImportedPdf.document_id}/download`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs underline inline-flex items-center gap-1"
              style={{ color: "var(--oh-accent-text)" }}
            >
              <Download className="w-3 h-3" />
              Télécharger / prévisualiser le PDF
            </a>
          )}
        </div>
      )}
    </div>
  );
}


// CC-17 — Block "Générer plan auto depuis photos" : 3 inputs (L, l, mur porte)
// + bouton. POST /plan-2d/auto-generate, transmet la réponse au parent.
// Stopgap : import manuel d'un plan 2D / croquis (tant que la génération auto
// n'est pas au point). Upload image ou PDF -> infos_dal.plan_2d + croquis_url.
function ImportPlan2DBlock({ dossierId, tenantId, onImported }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [imported, setImported] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ok = (file.type || "").startsWith("image/") || file.type === "application/pdf"
      || /\.(png|jpe?g|webp|pdf)$/i.test(file.name || "");
    if (!ok) { toast.error("Image (PNG/JPG) ou PDF uniquement"); e.target.value = ""; return; }
    setUploading(true);
    try {
      const token = localStorage.getItem("session_token");
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/dal/upload-plan-2d/${dossierId}`,
        { credentials: "include",
          method: "POST",
          headers: { "X-Tenant-ID": tenantId || "tef", ...(token && { Authorization: `Bearer ${token}` }) },
          credentials: "include",
          body: fd,
        }
      );
      if (res.ok) {
        const data = await res.json();
        setImported(data);
        toast.success("Plan 2D / croquis importé");
        onImported?.(data);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Erreur import (HTTP ${res.status})`);
      }
    } catch (err) {
      toast.error(`Erreur réseau : ${err.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="mb-4 p-4 rounded-lg border" style={surfaceCard} data-testid="dal-import-plan2d-block">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Upload className="w-4 h-4" style={{ color: "var(--oh-accent-text)" }} />
          <span className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>Importer le plan 2D / croquis</span>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileInputRef} type="file" accept="image/*,application/pdf,.pdf" onChange={handleUpload} className="hidden" data-testid="dal-import-plan2d-file-input" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} size="sm" variant="outline" style={btnSecondary} data-testid="dal-import-plan2d-button">
            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            {imported ? "Remplacer le plan" : "Importer un plan / croquis"}
          </Button>
        </div>
      </div>
      <p className="text-xs mt-2" style={{ color: "var(--oh-text-muted)" }}>
        Attache un plan 2D ou un croquis (image ou PDF) au dossier, sans quitter le DAL.
      </p>
      {imported?.url && (
        <div className="mt-3 text-xs flex items-center gap-2" style={{ color: "var(--oh-text-secondary)" }}>
          <CheckCircle2 className="w-3 h-3" style={{ color: "var(--oh-success-text)" }} />
          <a href={`${process.env.REACT_APP_BACKEND_URL}${imported.url}`} target="_blank" rel="noreferrer" className="underline" style={{ color: "var(--oh-accent-text)" }}>
            {imported.filename || "Plan importé"}
          </a>
        </div>
      )}
    </div>
  );
}

function Plan2DAutoBlock({ dossierId, tenantId, photos, zones, onAutoReady }) {
  const sdb = (zones || []).find(z => z.id === "salle_de_bain") || {};
  const fields = sdb.fields || {};
  const [longueur, setLongueur] = useState(parseInt(fields.longueur, 10) || 350);
  const [largeur, setLargeur] = useState(parseInt(fields.largeur, 10) || 220);
  const [mur, setMur] = useState("nord");
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("");

  const photoIds = (photos || [])
    .filter(p => !p.zone || p.zone === "salle_de_bain" || p.zone === "sdb")
    .map(p => p.id)
    .filter(Boolean);

  const generate = async () => {
    if (!photoIds.length) {
      toast.error("Aucune photo salle de bain — uploader d'abord des photos");
      return;
    }
    setLoading(true);
    setNotes("");
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(
        `${API}/api/metier/tef/dossiers/${dossierId}/plan-2d/auto-generate`,
        { credentials: "include",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-ID": tenantId || "tef",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          credentials: "include",
          body: JSON.stringify({
            photo_ids: photoIds.slice(0, 8),
            longueur_cm: parseInt(longueur, 10),
            largeur_cm: parseInt(largeur, 10),
            mur_porte: mur,
          }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        setNotes(data.llm_notes || "");
        onAutoReady?.(data.plan_2d_data);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Erreur ${res.status}`);
      }
    } catch (e) {
      toast.error(`Erreur réseau : ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-4 p-3 rounded-lg border border-[var(--oh-info-border)] bg-[var(--oh-info-bg)]">
      <div className="flex items-center gap-2 mb-2">
        <Wand2 className="w-4 h-4 text-[var(--oh-info-text)]" />
        <span className="text-sm font-medium text-[var(--oh-info-text)]">
          Générer plan auto depuis photos (Vision IA)
        </span>
      </div>
      {/* CC-17 Option B (2026-05-17) — avertissement pièces non rectangulaires */}
      <div className="mb-2 p-2 rounded border border-[var(--oh-accent)] bg-[var(--oh-accent-bg)] text-xs text-[var(--oh-accent-text)] flex items-start gap-2"
           data-testid="plan2d-shape-warning">
        <Info className="w-4 h-4 mt-0.5 text-[var(--oh-accent-text)] shrink-0" aria-hidden />
        <div>
          La génération auto suppose une <strong>pièce rectangulaire</strong> (L × l).
          Pour les pièces en <strong>L, T</strong> ou avec décrochements, utilisez le
          <strong> mode manuel</strong> de l'éditeur ci-dessous (Konva).
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2">
        <div>
          <Label className="text-xs text-[var(--oh-text-secondary)]">Longueur (cm)</Label>
          <Input type="number" value={longueur}
                 onChange={(e) => setLongueur(e.target.value)}
                 className="h-8 bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                 data-testid="plan2d-auto-longueur" />
        </div>
        <div>
          <Label className="text-xs text-[var(--oh-text-secondary)]">Largeur (cm)</Label>
          <Input type="number" value={largeur}
                 onChange={(e) => setLargeur(e.target.value)}
                 className="h-8 bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                 data-testid="plan2d-auto-largeur" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs text-[var(--oh-text-secondary)]">Mur de la porte</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
            {["nord", "sud", "est", "ouest"].map(m => (
              <button
                key={m}
                onClick={() => setMur(m)}
                className="h-8 text-xs rounded capitalize border"
                style={mur === m
                  ? { background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)", borderColor: "var(--oh-accent)" }
                  : { background: "var(--oh-bg-elevated)", color: "var(--oh-text-secondary)", borderColor: "var(--oh-border)" }}
                data-testid={`plan2d-auto-mur-${m}`}
              >{m}</button>
            ))}
          </div>
        </div>
      </div>
      <Button
        onClick={generate}
        disabled={loading || !photoIds.length}
        variant="outline"
        style={btnSecondary}
        className="w-full h-8 text-xs"
        data-testid="btn-plan2d-auto-generate"
      >
        {loading
          ? "Analyse en cours (~5s)…"
          : `Générer le plan depuis ${photoIds.length} photo${photoIds.length > 1 ? "s" : ""}`}
      </Button>
      {notes && (
        <p className="text-xs text-[var(--oh-info-text)] mt-2 italic">IA : {notes}</p>
      )}
    </div>
  );
}

const API = process.env.REACT_APP_BACKEND_URL;

// History icon
const HistoryIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/></svg>
);

const ZONES_STANDARD = [
  { id: "exterieur", label: "Extérieur / Accès" },
  { id: "entree", label: "Entrée / Couloirs" },
  { id: "salon", label: "Salon / Salle à manger / Cuisine" },
  { id: "chambre", label: "Chambre du demandeur" },
  { id: "garage", label: "Garage / Grenier / Cave" },
  { id: "balcon", label: "Balcon / Terrasse" },
  { id: "escalier", label: "Escalier" },
  { id: "volets", label: "Volets" },
  { id: "sanitaires", label: "Sanitaires" },
  { id: "salle_de_bain", label: "Salle de bain", critical: true },
];

const ETAT_OPTIONS = [
  { value: "travaux_non_necessaires", label: "Travaux non nécessaires" },
  { value: "travaux_necessaires", label: "Travaux nécessaires" },
  { value: "non_evalue", label: "Non évalué" },
];

// Score de solidité — encart d'état charte (paires sémantiques dual-mode) :
// solide → succès, à justifier → info/alerte, risque ANAH → erreur.
const SCORE_CONFIG = {
  solide: { label: "Solide", style: { background: "var(--oh-success-bg)", color: "var(--oh-success-text)", border: "1px solid var(--oh-success-border)" }, icon: ShieldCheck },
  a_justifier: { label: "À justifier", style: { background: "var(--oh-info-bg)", color: "var(--oh-info-text)", border: "1px solid var(--oh-info-border)" }, icon: AlertTriangle },
  risque_anah: { label: "Risque ANAH", style: { background: "var(--oh-error-bg)", color: "var(--oh-error-text)", border: "1px solid var(--oh-error-border)" }, icon: AlertCircle },
};

// Niveau de risque IA → même famille sémantique (badge encart d'état).
const RISK_STYLE = {
  eleve: { background: "var(--oh-error-bg)", color: "var(--oh-error-text)", border: "1px solid var(--oh-error-border)" },
  moyen: { background: "var(--oh-info-bg)", color: "var(--oh-info-text)", border: "1px solid var(--oh-info-border)" },
  faible: { background: "var(--oh-success-bg)", color: "var(--oh-success-text)", border: "1px solid var(--oh-success-border)" },
};

function getAuthHeaders() {
  const token = localStorage.getItem("session_token");
  const tenantId = localStorage.getItem("tenant_id");
  return {
    Authorization: `Bearer ${token}`,
    "X-Tenant-ID": tenantId || "tef",
    "Content-Type": "application/json",
  };
}

// --- BOUTON IA TRANSCRIRE ---
function AITranscribeButton({ value, onResult, context }) {
  const [loading, setLoading] = useState(false);
  const handleTranscribe = async () => {
    if (!value?.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/tef/dal/transcribe`, { credentials: "include",
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ text: value, context: context || "" }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.result) onResult(data.result);
      }
    } catch (err) { console.error("Erreur transcription IA:", err); }
    finally { setLoading(false); }
  };
  if (!value?.trim()) return null;
  return (
    <Button type="button" size="sm" variant="ghost" onClick={handleTranscribe} disabled={loading}
      className="text-[var(--oh-accent-text)] hover:text-[var(--oh-accent-text)] text-xs h-6 px-2 gap-1" title="Réécrire en français clair via IA">
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      <span className="hidden sm:inline">Transcrire</span>
    </Button>
  );
}

// --- SECTION WRAPPER ---
function Section({ title, icon: Icon, open, onToggle, badge, children, id }) {
  return (
    <Collapsible open={open} onOpenChange={onToggle}>
      <CollapsibleTrigger className="w-full flex items-center justify-between p-3 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg hover:bg-[var(--oh-bg-elevated)] transition-colors print:bg-transparent print:border-none print:p-0" data-testid={`dal-section-${(id || title).toLowerCase().replace(/\s/g, '-')}`}>
        <div className="flex items-center gap-2">
          <span className="print:hidden">{open ? <ChevronDown className="w-4 h-4 text-[var(--oh-text-secondary)]" /> : <ChevronRight className="w-4 h-4 text-[var(--oh-text-secondary)]" />}</span>
          {Icon && <Icon className="w-4 h-4 text-[var(--oh-info-text)]" />}
          <span className="text-[var(--oh-text)] font-medium text-sm">{title}</span>
        </div>
        <span className="print:hidden">{badge}</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1 print:mt-2">
        <div className="p-4 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg print:bg-transparent print:border-none print:p-0">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function DALForm({ dossierId, reference, tenantId, client, infosLogement, onUpdate }) {
  const { items: occupationOptions } = useConfigList("statuts_occupation");
  const { items: logementTypeOptions } = useConfigList("types_logement");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingPipeline, setGeneratingPipeline] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dalGenere, setDalGenere] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  // P-020 — Rapport de conformité (INTERNE, jamais ANAH)
  const [showComplianceReport, setShowComplianceReport] = useState(false);
  const [complianceReport, setComplianceReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [pushingReport, setPushingReport] = useState(false);
  // Phase 1 — Import DAL PDF existant (JJ 2026-05-20)
  const [dalImportedPdf, setDalImportedPdf] = useState(null);
  const printRef = useRef(null);

  // P-020 — ouvrir le rapport de conformité (INTERNE) + option push Zoho 01_ADMIN
  const openComplianceReport = async () => {
    setShowComplianceReport(true);
    setReportLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal/compliance-report`, { credentials: "include", headers: getAuthHeaders() });
      const data = res.ok ? await res.json() : { disponible: false, message: "Rapport indisponible" };
      setComplianceReport(data);
      if (!data.disponible) toast.info("Aucun rapport — lancez l'agent DAL d'abord");
    } catch (e) {
      toast.error("Erreur: " + e.message);
      setComplianceReport({ disponible: false, message: "Erreur réseau" });
    } finally {
      setReportLoading(false);
    }
  };
  const pushComplianceReportToZoho = async () => {
    setPushingReport(true);
    try {
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal/compliance-report/generer`, { credentials: "include", method: "POST", headers: getAuthHeaders() });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Rapport conformité poussé sur Zoho (${data.zoho_subfolder})${data.skipped_preprod ? " — preprod: dry-run" : ""}`);
      } else {
        toast.error(data.detail || "Échec génération/push du rapport");
      }
    } catch (e) {
      toast.error("Erreur: " + e.message);
    } finally {
      setPushingReport(false);
    }
  };

  const [openSections, setOpenSections] = useState({
    beneficiaire: true, handicap: false, besoins: false, zones: false,
    solutions: false, pipeline: false, photos: false, plan2d: false, aiAfter: false
  });

  // --- BENEFICIAIRE (from client) ---
  // P0-09 fix BUG-11 (2026-05-03) — alignement sur modele Client canonique :
  // nbre_occupants -> nb_personnes_foyer (alias Client.nb_personnes_foyer).
  // situation_sociale (familiale) et taux_prise_en_charge restent specifiques DAL.
  const [beneficiaire, setBeneficiaire] = useState({
    prenom: "", nom: "", email: "", telephone: "",
    date_naissance: "", situation_sociale: "", nb_personnes_foyer: "1",
    taux_prise_en_charge: "",
  });

  // --- LOGEMENT ---
  // P0-09 fix BUG-11 (2026-05-03) — alignement sur modele Client canonique :
  // type_habitation -> type_logement, statut_occupant -> statut_occupation,
  // nombre_niveaux -> nb_niveaux, nombre_pieces -> nb_pieces.
  // zone_logement reste specifique DAL (rural/urbain/periurbain), pas dans Client.
  const [logement, setLogement] = useState({
    type_logement: "appartement", annee_construction: "",
    statut_occupation: "proprietaire", nb_niveaux: "1",
    surface_habitable: "", zone_logement: "urbaine", nb_pieces: "",
  });

  // --- SITUATION HANDICAP ---
  const [handicap, setHandicap] = useState({
    reconnaissance_handicap: "non_evalue",
    equipement_deplacement: "non",
    equipements_detail: "",
    regime_retraite: "general",
    evaluation_ergotherapeute: "non",
    condition_cas: "non",
  });

  // --- BESOINS ---
  const [besoins, setBesoins] = useState({
    age: "", mobilite: "autonome", usage_reel: "",
    historique_chutes: "", difficultes_quotidiennes: "",
    gir: "", apa: false, rqth: false,
  });

  // --- DATE VISITE ---
  const [dateVisite, setDateVisite] = useState("");

  // --- SOURCE UNIQUE ---
  // canonical : 7 valeurs canoniques (depuis API GET /dal). Si renseigné en
  // fiche dossier/client, le DAL affiche en lecture seule (le DAL est un
  // formulaire de visite, pas de création). Cf. dal_source_unique.py backend.
  const [canonical, setCanonical] = useState({});
  // categorie_ressources MPA calculée auto par le backend (RFR + nb_personnes
  // + zone code postal). Jamais saisie. Affichée en lecture seule.
  const [categorieRessources, setCategorieRessources] = useState(null);

  // Vrai si la source canonique (fiche dossier/client) renseigne déjà le
  // champ — l'input DAL doit alors être en lecture seule pour empêcher la
  // double saisie. Empty string et null comptent comme "non renseigné".
  // Note : `false` (apa) compte comme renseigné — c'est une valeur explicite.
  const isLockedCanonical = (key) => {
    const v = canonical?.[key];
    return v !== null && v !== undefined && v !== "";
  };
  // Pour les 7 champs canoniques, source de vérité = `canonical` quand
  // verrouillé. Le state local n'est utilisé qu'en mode édition (cas où
  // la fiche dossier n'a pas la valeur — exceptionnel). Évite que
  // `nb_personnes_foyer` ou `date_naissance` apparaissent vides quand
  // metier_clients a la valeur mais que le DAL n'a jamais été sauvegardé.
  const canonicalDisplay = (key, fallback) => {
    if (!isLockedCanonical(key)) return fallback;
    const v = canonical?.[key];
    return v == null ? "" : v;
  };
  // Calcule l'âge en années depuis une date ISO YYYY-MM-DD.
  const computeAge = (isoDate) => {
    if (!isoDate) return null;
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
    return age;
  };
  const lockedHint = "🔒 Renseigné depuis la fiche dossier (modifier dans la fiche)";

  // --- ZONES ---
  const [zones, setZones] = useState(
    ZONES_STANDARD.map(z => ({
      ...z, etat: "non_evalue", adapte: true, eligible_subventions: false,
      problematiques: [], preconisations: [], notes: ""
    }))
  );

  // --- SOLUTIONS & PIPELINE ---
  const [solutions, setSolutions] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  // CC-27 amélioration 2 : suggestions problème→solution depuis
  // metier_config_listes (chargées via /api/metier/tef/config/dal-mappings).
  // Source unique : zéro hardcode côté frontend.
  const [problemSolutionMappings, setProblemSolutionMappings] = useState([]);
  const [suggestActiveIdx, setSuggestActiveIdx] = useState(null);
  const [photos, setPhotos] = useState([]);
  // CC-17 — Plan 2D auto : pré-rempli par Plan2DAutoBlock → passé à Plan2DGenerator
  const [plan2DInitialData, setPlan2DInitialData] = useState(null);
  const [zone3dImages, setZone3dImages] = useState({});       // { zone_id: { url, image_base64 } }
  const [generatingZone3d, setGeneratingZone3d] = useState({}); // { zone_id: bool }
  const [generatingAllZones3d, setGeneratingAllZones3d] = useState(false);
  const [intervenant, setIntervenant] = useState({ nom: "", prenom: "", fonction: "" });
  const [observations, setObservations] = useState("");
  const [visiteurs, setVisiteurs] = useState([]);
  const [dalHistory, setDalHistory] = useState([]);

  // --- LOAD ---
  useEffect(() => { loadDAL(); loadVisiteurs(); loadMappingsConfig(); }, [dossierId]);

  // --- LOAD MAPPINGS CONFIG (CC-27) ---
  const loadMappingsConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/metier/tef/config/dal-mappings`, { credentials: "include", headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data?.mappings)) setProblemSolutionMappings(data.mappings);
      }
    } catch (_) { /* silencieux : autocomplete optionnel */ }
  }, []);

  // Normalise une chaîne pour le matching (sans accents, lowercase, trim).
  const normalizeText = (s) => (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .trim();

  // Calcule les suggestions pour un texte saisi : matche `mots_cles` ou
  // `probleme` du mapping. Renvoie max 5 résultats triés par pertinence.
  const computeSuggestions = useCallback((query) => {
    const q = normalizeText(query);
    if (q.length < 2) return [];
    const scored = [];
    for (const m of problemSolutionMappings) {
      const probNorm = normalizeText(m.probleme);
      const motsNorm = (m.mots_cles || []).map(normalizeText);
      // Score : 3 = match exact mot-clé, 2 = mot-clé contient q, 1 = probleme contient q
      let score = 0;
      if (motsNorm.includes(q)) score = 3;
      else if (motsNorm.some(k => k.includes(q) || q.includes(k))) score = 2;
      else if (probNorm.includes(q)) score = 1;
      if (score > 0) scored.push({ m, score });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 5).map(x => x.m);
  }, [problemSolutionMappings]);

  const loadDAL = useCallback(async () => {
    if (!dossierId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal`, { credentials: "include", headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        // Source unique : valeurs canoniques (read-only quand renseignées)
        // + catégorie ressources MPA calculée. Cf. backend tef/routes.py.
        if (data.canonical) setCanonical(data.canonical);
        if (data.categorie_ressources) setCategorieRessources(data.categorie_ressources);
        // Map API fields to frontend state
        if (data.client) setBeneficiaire(prev => ({ ...prev, ...data.client }));
        if (data.beneficiaire) setBeneficiaire(prev => ({ ...prev, ...data.beneficiaire }));
        if (data.infos_logement) setLogement(prev => ({ ...prev, ...data.infos_logement }));
        if (data.logement) setLogement(prev => ({ ...prev, ...data.logement }));
        if (data.besoins) setBesoins(prev => ({ ...prev, ...data.besoins }));
        if (data.handicap) setHandicap(prev => ({ ...prev, ...data.handicap }));
        if (data.date_visite) setDateVisite(data.date_visite);
        if (data.zones_evaluees?.length) {
          // Merge with standard zones to add new ones
          const merged = ZONES_STANDARD.map(std => {
            const existing = data.zones_evaluees.find(z => z.id === std.id);
            return existing ? { ...std, ...existing } : {
              ...std, etat: "non_evalue", adapte: true, eligible_subventions: false,
              problematiques: [], preconisations: [], notes: ""
            };
          });
          setZones(merged);
        }
        if (data.solutions_mapping?.length) setSolutions(data.solutions_mapping);
        // Plan 2D — charger les équipements persistés (fix 2026-05-28).
        // Avant : plan2DInitialData restait null tant que l'auto-gen IA
        // n'avait pas tourné, donc les équipements du seed/save manuel
        // n'apparaissaient jamais sur le canvas.
        if (data.plan_2d?.equipments?.length || data.plan_2d?.dims) {
          setPlan2DInitialData(data.plan_2d);
        }
        if (data.pipeline) setPipeline(data.pipeline);
        if (data.photos?.length) setPhotos(data.photos);
        if (data.zones_3d_images) {
          // Reconstruire le state zone3dImages depuis les données sauvegardées
          const loaded = {};
          for (const [zid, zdata] of Object.entries(data.zones_3d_images || {})) {
            loaded[zid] = { url: zdata.url, zone_name: zdata.zone_name, preconisations: zdata.preconisations, style: zdata.style, created_at: zdata.created_at };
          }
          if (Object.keys(loaded).length) setZone3dImages(loaded);
        }
        if (data.intervenant) setIntervenant(data.intervenant);
        if (data.observations) setObservations(data.observations);
        if (data.dal_genere) { setDalGenere(true); setDownloadUrl(data.download_url); }
        // Import DAL PDF (Phase 1 — JJ 2026-05-20) : flag stocké dans
        // workflow_data.tef.infos_dal.dal_imported_pdf
        if (data.dal_imported_pdf) setDalImportedPdf(data.dal_imported_pdf);
      }
    } catch (err) { console.error("Erreur chargement DAL:", err); }
    finally { setLoading(false); }
  }, [dossierId]);

  const loadVisiteurs = useCallback(async () => {
    // BUG-03 : précédemment, on appelait /api/operation/visiteurs (collection vide)
    // puis fallback /api/management/users qui listait des admins (Stéphanie, JJ) au
    // lieu des visiteurs AMO terrain (Jordan, Kevin). Source unique : op_visiteurs
    // via /api/metier/lookup/visiteurs — même endpoint que MobileDALWizard pour
    // garantir une liste cohérente entre mobile et desktop.
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API}/api/metier/lookup/visiteurs`, { credentials: "include", headers });
      if (res.ok) {
        const data = await res.json();
        setVisiteurs(mapLookupVisiteursResponse(data));
      }
    } catch (e) { console.error("Load visiteurs err:", e); }
  }, []);

  const loadDalHistory = useCallback(async () => {
    if (!dossierId) return;
    try {
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal/history`, { credentials: "include", headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDalHistory(data.history || []);
      }
    } catch (e) { console.error("Load history err:", e); }
  }, [dossierId]);

  // Pre-fill from client data
  useEffect(() => {
    if (client) {
      const updates = {};
      if (client.prenom) updates.prenom = client.prenom;
      if (client.nom) updates.nom = client.nom;
      if (client.email) updates.email = client.email;
      if (client.telephone) updates.telephone = client.telephone;
      if (client.date_naissance) {
        updates.date_naissance = client.date_naissance;
        const birth = new Date(client.date_naissance);
        const age = Math.floor((Date.now() - birth.getTime()) / 31557600000);
        if (age > 0 && age < 120) setBesoins(prev => ({ ...prev, age: String(age) }));
      }
      if (client.situation_sociale) updates.situation_sociale = client.situation_sociale;
      // P0-09 fix BUG-11 (2026-05-03) — sync depuis nb_personnes_foyer canonique
      if (client.nb_personnes_foyer != null) updates.nb_personnes_foyer = String(client.nb_personnes_foyer);
      if (Object.keys(updates).length) setBeneficiaire(prev => ({ ...prev, ...updates }));
    }
  }, [client]);

  // Pre-fill logement from infosLogement (P0-09 BUG-11 — accepte alias legacy)
  useEffect(() => {
    if (infosLogement) {
      const updates = {};
      // type_habitation (legacy) -> type_logement (canonique)
      const typeLog = infosLogement.type_logement || infosLogement.type_habitation;
      if (typeLog) updates.type_logement = typeLog;
      if (infosLogement.annee_construction) updates.annee_construction = infosLogement.annee_construction;
      // statut_occupant (legacy) -> statut_occupation (canonique)
      const stOcc = infosLogement.statut_occupation || infosLogement.statut_occupant;
      if (stOcc) updates.statut_occupation = stOcc;
      // nombre_niveaux (legacy) -> nb_niveaux (canonique)
      const nbNiv = infosLogement.nb_niveaux || infosLogement.nombre_niveaux;
      if (nbNiv != null) updates.nb_niveaux = nbNiv;
      const surface = infosLogement.surface_habitable || infosLogement.surface;
      if (surface) updates.surface_habitable = surface;
      const zone = infosLogement.zone_logement || infosLogement.zone;
      if (zone) updates.zone_logement = zone;
      // nombre_pieces (legacy) -> nb_pieces (canonique)
      const nbPces = infosLogement.nb_pieces || infosLogement.nombre_pieces;
      if (nbPces != null) updates.nb_pieces = nbPces;
      if (Object.keys(updates).length) setLogement(prev => ({ ...prev, ...updates }));
    }
  }, [infosLogement]);

  // --- SAVE ---
  const saveDAL = async () => {
    setSaving(true);
    try {
      const body = {
        beneficiaire, logement, handicap, besoins, date_visite: dateVisite,
        zones_evaluees: zones, solutions_mapping: solutions,
        pipeline, photos, intervenant, observations,
        zones_3d_images: Object.fromEntries(
          Object.entries(zone3dImages).map(([k, v]) => [k, {
            url: v.url,
            zone_name: v.zone_name,
            preconisations: v.preconisations,
            style: v.style,
            created_at: v.created_at
          }])
        ),
      };
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal`, { credentials: "include",
        method: "PUT", headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (res.ok && onUpdate) onUpdate();
    } catch (err) { console.error("Erreur sauvegarde DAL:", err); }
    finally { setSaving(false); }
  };

  // --- GENERATE ZONE 3D (IA) ---
  const generateZone3D = async (zone) => {
    if (!zone.preconisations?.length) return;
    setGeneratingZone3d(prev => ({ ...prev, [zone.id]: true }));
    try {
      const res = await fetch(`${API}/api/dal/generate-zone-3d/${dossierId}`, { credentials: "include",
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          zone_id: zone.id,
          zone_name: zone.label,
          preconisations: zone.preconisations,
          style: "realistic"
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setZone3dImages(prev => ({
          ...prev,
          [zone.id]: {
            url: data.image_url,
            image_base64: data.image_base64,
            zone_name: zone.label,
            preconisations: zone.preconisations,
            style: "realistic",
            created_at: new Date().toISOString()
          }
        }));
      } else {
        console.error("Erreur génération 3D zone:", await res.text());
      }
    } catch (err) {
      console.error("Erreur génération 3D:", err);
    } finally {
      setGeneratingZone3d(prev => ({ ...prev, [zone.id]: false }));
    }
  };

  const generateAllZones3D = async () => {
    const zonesAvecPrecos = zones.filter(z => z.etat === "travaux_necessaires" && z.preconisations?.length > 0);
    if (!zonesAvecPrecos.length) return;
    setGeneratingAllZones3d(true);
    for (const zone of zonesAvecPrecos) {
      await generateZone3D(zone);
    }
    setGeneratingAllZones3d(false);
  };

  // --- GENERATE PIPELINE (IA) ---
  const generatePipeline = async () => {
    setGeneratingPipeline(true);
    try {
      await saveDAL();
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal/generate-pipeline`, { credentials: "include",
        method: "POST", headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.pipeline) {
          setPipeline(data.pipeline);
          if (!solutions.length && data.pipeline.risques?.length) {
            setSolutions(data.pipeline.risques.map(r => ({
              probleme: r.risque, solution: "", zone: r.zone
            })));
          }
        }
      }
    } catch (err) { console.error("Erreur pipeline:", err); }
    finally { setGeneratingPipeline(false); }
  };

  // --- REGENERATE MAPPING (CC-27 amélioration 1) ---
  // Relance l'IA sur les problèmes/préconisations actuels et reconstruit le
  // mapping 1:1 depuis les zones. Écrase les associations existantes après
  // confirmation utilisateur.
  const regenerateMapping = async () => {
    if (solutions.length > 0) {
      const ok = window.confirm("Régénérer le mapping ? Les associations actuelles seront remplacées.");
      if (!ok) return;
    }
    setGeneratingPipeline(true);
    try {
      await saveDAL();
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal/generate-pipeline`, { credentials: "include",
        method: "POST", headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.pipeline) setPipeline(data.pipeline);
      }
      // Reconstruit le mapping 1:1 depuis les zones (preco source de vérité)
      const next = buildSolutionsMappingFromZones(zones);
      setSolutions(next);
      toast.success(`Mapping régénéré (${next.length} association${next.length > 1 ? "s" : ""}).`);
    } catch (err) {
      toast.error("Erreur régénération : " + (err?.message || err));
    } finally { setGeneratingPipeline(false); }
  };

  // --- GENERATE PDF ---
  const generatePDF = async () => {
    setGenerating(true);
    try {
      await saveDAL();
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal/generer`, { credentials: "include",
        method: "POST", headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setDalGenere(true);
        setDownloadUrl(data.download_url);
      }
    } catch (err) { console.error("Erreur generation PDF:", err); }
    finally { setGenerating(false); }
  };

  // --- PRINT ---
  const handlePrint = () => {
    window.print();
  };

  // --- PHOTO UPLOAD ---
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("zone", "general");
        const token = localStorage.getItem("session_token");
        const tid = localStorage.getItem("tenant_id") || "tef";
        const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal/upload-photo`, { credentials: "include",
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "X-Tenant-ID": tid },
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          setPhotos(prev => [...prev, data]);
        }
      }
    } catch (err) { console.error("Erreur upload:", err); }
    finally { setUploading(false); }
  };

  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  const score = pipeline?.score_solidite;
  const scoreConfig = score ? SCORE_CONFIG[score] : null;

  const clientFullName = formatClientFullName(beneficiaire) || "—";
  const clientAddress = formatClientAddress(client) || "—";

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-[var(--oh-accent-text)] mr-2" />
      <span className="text-[var(--oh-text-secondary)]">Chargement DAL...</span>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="dal-form" ref={printRef}>
      {/* ===== EN-TETE CLIENT ===== */}
      <OhCard data-testid="dal-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium" style={{ color: "var(--oh-text)" }} data-testid="dal-client-name">{clientFullName}</h2>
            <div className="flex items-center gap-2 text-sm mt-1" style={{ color: "var(--oh-text-secondary)" }}>
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span>{clientAddress}</span>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {scoreConfig && (
              <Badge className="text-xs" style={scoreConfig.style} data-testid="dal-score">
                <scoreConfig.icon className="w-3 h-3 mr-1" />
                {scoreConfig.label}
              </Badge>
            )}
            <div className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5 text-[var(--oh-text-secondary)]" />
              <Input type="date"
                value={canonicalDisplay("date_visite", dateVisite) || ""}
                onChange={e => setDateVisite(e.target.value)}
                disabled={isLockedCanonical("date_visite")}
                title={isLockedCanonical("date_visite") ? lockedHint : undefined}
                className={`bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-7 text-xs w-36 ${isLockedCanonical("date_visite") ? "opacity-60 cursor-not-allowed" : ""}`}
                data-testid="dal-date-visite" />
            </div>
          </div>
        </div>
      </OhCard>

      {/* ===== TOOLBAR — hiérarchie charte : 1 primaire (Générer le DAL), reste discret/secondaire ===== */}
      <div className="flex gap-2 flex-wrap print:hidden">
        <Button size="sm" style={btnPrimary} onClick={generatePDF} disabled={generating} data-testid="dal-generate-pdf-btn">
          {generating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FileDown className="w-3 h-3 mr-1" />}
          Générer le DAL
        </Button>
        <Button size="sm" variant="outline" onClick={saveDAL} disabled={saving} data-testid="dal-save-btn">
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
          Enregistrer
        </Button>
        <Button size="sm" variant="outline" onClick={handlePrint} data-testid="dal-print-btn">
          <Printer className="w-3 h-3 mr-1" /> Imprimer
        </Button>
        {downloadUrl && (
          <Button size="sm" variant="outline" style={btnSecondary} data-testid="dal-download-btn"
            onClick={async () => {
              try {
                const resp = await fetch(`${API}${downloadUrl}`, { credentials: "include", headers: getAuthHeaders() });
                if (!resp.ok) throw new Error("Erreur téléchargement");
                const blob = await resp.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `DAL_${dossierId}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
              } catch (e) { toast.error("Erreur: " + e.message); }
            }}>
            <Download className="w-3 h-3 mr-1" /> Télécharger
          </Button>
        )}
        <Button size="sm" variant="outline" style={btnSecondary} onClick={openComplianceReport} data-testid="dal-compliance-report-btn" title="Document interne — ne part pas à l'ANAH">
          <ShieldCheck className="w-3 h-3 mr-1" /> Voir le rapport qualité
        </Button>
      </div>

      {/* ===== RAPPORT QUALITÉ (INTERNE, jamais ANAH) — P-020 ===== */}
      <Dialog open={showComplianceReport} onOpenChange={setShowComplianceReport}>
        <DialogContent style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", maxWidth: "640px" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
              <ShieldCheck className="w-5 h-5" style={{ color: "var(--oh-accent-text)" }} /> Rapport qualité — usage interne
            </DialogTitle>
            <DialogDescription style={{ color: "var(--oh-text-secondary)" }}>
              Garde-fou qualité TEF (complétude, alertes, conseils). Ce document ne part JAMAIS à l'ANAH.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-auto" style={{ color: "var(--oh-text)" }}>
            {reportLoading ? (
              <div className="flex items-center gap-2" style={{ color: "var(--oh-text-secondary)" }}>
                <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
              </div>
            ) : complianceReport?.disponible ? (
              <AgentMarkdown density="compact">{complianceReport.rapport}</AgentMarkdown>
            ) : (
              <p style={{ color: "var(--oh-text-muted)" }}>{complianceReport?.message || "Rapport indisponible."}</p>
            )}
          </div>
          <div className="flex justify-end pt-2">
            <Button size="sm" variant="outline" style={btnSecondary} onClick={pushComplianceReportToZoho} disabled={pushingReport || !complianceReport?.disponible} data-testid="dal-compliance-push-btn">
              {pushingReport ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileDown className="w-3 h-3 mr-1" />} Générer le PDF (Zoho 01_ADMIN)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== ALERTES ===== */}
      {pipeline?.alertes?.length > 0 && (
        <OhStatusNote variant="error" title="Alertes détectées">
          <div className="space-y-0.5">
            {pipeline.alertes.map((a, i) => (
              <p key={i} className="text-xs flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 shrink-0" /> {a}
              </p>
            ))}
          </div>
        </OhStatusNote>
      )}

      {/* ===== AGENT IA DAL — AG-02 (cache à l'impression) ===== */}
      <div className="print:hidden">
        <DALAgentPanel reference={reference} tenantId={tenantId} onRedige={generatePipeline} redigeLoading={generatingPipeline} />
      </div>

      {/* ===== IMPORT DAL PDF EXISTANT — Phase 1 (cache à l'impression) ===== */}
      <div className="print:hidden">
        <ImportDALPDFBlock
          dossierId={dossierId}
          tenantId={tenantId}
          dalImportedPdf={dalImportedPdf}
          onImported={(info) => setDalImportedPdf(info)}
        />
      </div>

      {/* ===== 1. BENEFICIAIRE + LOGEMENT ===== */}
      <Section title="Bénéficiaire & Logement" icon={User} id="beneficiaire"
        open={openSections.beneficiaire} onToggle={() => toggleSection("beneficiaire")}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* BENEFICIAIRE */}
          <div>
            <h4 className="font-medium text-sm mb-3" style={{ color: "var(--oh-text)" }}>Bénéficiaire</h4>
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[var(--oh-text-secondary)] text-xs">Prénom</Label>
                  <Input value={beneficiaire.prenom} onChange={e => setBeneficiaire(p => ({...p, prenom: e.target.value}))}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-[var(--oh-text-secondary)] text-xs">Nom</Label>
                  <Input value={beneficiaire.nom} onChange={e => setBeneficiaire(p => ({...p, nom: e.target.value}))}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm" />
                </div>
              </div>
              <div>
                <Label className="text-[var(--oh-text-secondary)] text-xs">Email</Label>
                <Input value={beneficiaire.email} onChange={e => setBeneficiaire(p => ({...p, email: e.target.value}))}
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[var(--oh-text-secondary)] text-xs">Téléphone</Label>
                  <Input value={beneficiaire.telephone} onChange={e => setBeneficiaire(p => ({...p, telephone: e.target.value}))}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-[var(--oh-text-secondary)] text-xs">
                    Date de naissance
                    {(() => {
                      const dn = canonicalDisplay("date_naissance", beneficiaire.date_naissance);
                      const age = computeAge(dn);
                      return age != null ? <span className="ml-2 text-[var(--oh-text-secondary)]">— {age} ans</span> : null;
                    })()}
                  </Label>
                  <Input type="date"
                    value={canonicalDisplay("date_naissance", beneficiaire.date_naissance) || ""}
                    onChange={e => setBeneficiaire(p => ({...p, date_naissance: e.target.value}))}
                    disabled={isLockedCanonical("date_naissance")}
                    data-testid="dal-date-naissance"
                    className={`bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm ${isLockedCanonical("date_naissance") ? "opacity-60 cursor-not-allowed" : ""}`} />
                  {isLockedCanonical("date_naissance") && (
                    <p className="text-[10px] text-[var(--oh-text-muted)] mt-0.5">{lockedHint}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[var(--oh-text-secondary)] text-xs">Situation sociale</Label>
                  <Select value={beneficiaire.situation_sociale || ""} onValueChange={v => setBeneficiaire(p => ({...p, situation_sociale: v}))}>
                    <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="celibataire">Célibataire</SelectItem>
                      <SelectItem value="marie">Marié(e)</SelectItem>
                      <SelectItem value="veuf">Veuf/Veuve</SelectItem>
                      <SelectItem value="divorce">Divorcé(e)</SelectItem>
                      <SelectItem value="pacse">Pacsé(e)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[var(--oh-text-secondary)] text-xs">Nbre d'occupant(s)</Label>
                  <Input
                    value={canonicalDisplay("nb_personnes_foyer", beneficiaire.nb_personnes_foyer) ?? ""}
                    onChange={e => setBeneficiaire(p => ({...p, nb_personnes_foyer: e.target.value}))}
                    disabled={isLockedCanonical("nb_personnes_foyer")}
                    data-testid="dal-nb-personnes-foyer"
                    className={`bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm ${isLockedCanonical("nb_personnes_foyer") ? "opacity-60 cursor-not-allowed" : ""}`} />
                  {isLockedCanonical("nb_personnes_foyer") && (
                    <p className="text-[10px] text-[var(--oh-text-muted)] mt-0.5">{lockedHint}</p>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-[var(--oh-text-secondary)] text-xs">Taux prise en charge (calculé)</Label>
                {/* Calculé auto par le backend (RFR + nb_personnes + zone CP).
                    Jamais saisi manuellement — source unique fiche dossier. */}
                <div
                  className={`h-8 px-3 rounded-md border flex items-center text-sm ${
                    categorieRessources?.computable
                      ? "bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                      : "bg-[var(--oh-info-bg)] border-[var(--oh-info-border)] text-[var(--oh-info-text)]"
                  }`}
                  data-testid="dal-categorie-ressources"
                  title="Calculé depuis RFR + nb personnes du foyer + zone code postal"
                >
                  {categorieRessources?.label || "—"}
                </div>
                {!categorieRessources?.computable && categorieRessources?.missing?.length > 0 && (
                  <p className="text-[10px] text-[var(--oh-info-text)] mt-0.5">
                    Compléter dans la fiche dossier : {categorieRessources.missing.join(", ")}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* LOGEMENT */}
          <div>
            <h4 className="font-medium text-sm mb-3" style={{ color: "var(--oh-text)" }}>Logement</h4>
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[var(--oh-text-secondary)] text-xs">Type d'habitation</Label>
                  <Select value={canonicalDisplay("type_logement", logement.type_logement) || ""}
                    onValueChange={v => setLogement(p => ({...p, type_logement: v}))}
                    disabled={isLockedCanonical("type_logement")}>
                    <SelectTrigger data-testid="dal-type-logement" className={`bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm ${isLockedCanonical("type_logement") ? "opacity-60 cursor-not-allowed" : ""}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {logementTypeOptions.map(o => (
                        <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isLockedCanonical("type_logement") && (
                    <p className="text-[10px] text-[var(--oh-text-muted)] mt-0.5">{lockedHint}</p>
                  )}
                </div>
                <div>
                  <Label className="text-[var(--oh-text-secondary)] text-xs">Année construction</Label>
                  <Input value={logement.annee_construction} onChange={e => setLogement(p => ({...p, annee_construction: e.target.value}))}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm" placeholder="ex: 1970" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[var(--oh-text-secondary)] text-xs">Statut occupant</Label>
                  <Select value={canonicalDisplay("statut_occupation", logement.statut_occupation) || ""}
                    onValueChange={v => setLogement(p => ({...p, statut_occupation: v}))}
                    disabled={isLockedCanonical("statut_occupation")}>
                    <SelectTrigger data-testid="dal-statut-occupation" className={`bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm ${isLockedCanonical("statut_occupation") ? "opacity-60 cursor-not-allowed" : ""}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {occupationOptions.map(o => (
                        <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isLockedCanonical("statut_occupation") && (
                    <p className="text-[10px] text-[var(--oh-text-muted)] mt-0.5">{lockedHint}</p>
                  )}
                </div>
                <div>
                  <Label className="text-[var(--oh-text-secondary)] text-xs">Nombre de niveaux</Label>
                  <Input value={logement.nb_niveaux} onChange={e => setLogement(p => ({...p, nb_niveaux: e.target.value}))}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[var(--oh-text-secondary)] text-xs">Surface habitable (m²)</Label>
                  <Input value={logement.surface_habitable} onChange={e => setLogement(p => ({...p, surface_habitable: e.target.value}))}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-[var(--oh-text-secondary)] text-xs">Zone du logement</Label>
                  <Select value={logement.zone_logement} onValueChange={v => setLogement(p => ({...p, zone_logement: v}))}>
                    <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urbaine">Urbaine</SelectItem>
                      <SelectItem value="rurale">Rurale</SelectItem>
                      <SelectItem value="periurbaine">Périurbaine</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[var(--oh-text-secondary)] text-xs">Nombre de pièces</Label>
                <Input value={logement.nb_pieces} onChange={e => setLogement(p => ({...p, nb_pieces: e.target.value}))}
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm" placeholder="ex: F3" />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== 2. SITUATION HANDICAP ===== */}
      <Section title="Situation Handicap" icon={Heart} id="handicap"
        open={openSections.handicap} onToggle={() => toggleSection("handicap")}>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[var(--oh-text-secondary)] text-xs">Reconnaissance administrative de handicap</Label>
              <Select value={handicap.reconnaissance_handicap} onValueChange={v => setHandicap(p => ({...p, reconnaissance_handicap: v}))}>
                <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="oui">Oui</SelectItem>
                  <SelectItem value="non">Non</SelectItem>
                  <SelectItem value="non_evalue">Non évalué</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[var(--oh-text-secondary)] text-xs">Utilise-t-il un équipement ? (fauteuil, canne...)</Label>
              <Select value={handicap.equipement_deplacement} onValueChange={v => setHandicap(p => ({...p, equipement_deplacement: v}))}>
                <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="oui">Oui</SelectItem>
                  <SelectItem value="non">Non</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {handicap.equipement_deplacement === "oui" && (
            <div>
              <Label className="text-[var(--oh-text-secondary)] text-xs">Si oui, quel(s) équipement(s) ?</Label>
              <Input value={handicap.equipements_detail} onChange={e => setHandicap(p => ({...p, equipements_detail: e.target.value}))}
                placeholder="Ex: canne, déambulateur..." className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm" />
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[var(--oh-text-secondary)] text-xs">Régime de retraite</Label>
              <Select value={handicap.regime_retraite} onValueChange={v => setHandicap(p => ({...p, regime_retraite: v}))}>
                <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">Général</SelectItem>
                  <SelectItem value="agricole">Agricole (MSA)</SelectItem>
                  <SelectItem value="rsi">RSI / indépendant</SelectItem>
                  <SelectItem value="fonctionnaire">Fonctionnaire</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[var(--oh-text-secondary)] text-xs">Évaluation d'un ergothérapeute (6 derniers mois) ?</Label>
              <Select value={handicap.evaluation_ergotherapeute} onValueChange={v => setHandicap(p => ({...p, evaluation_ergotherapeute: v}))}>
                <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="oui">Oui</SelectItem>
                  <SelectItem value="non">Non</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[var(--oh-text-secondary)] text-xs">Le demandeur satisfait-il aux conditions (Cas n°1 ou Cas n°2) ?</Label>
            <Select value={handicap.condition_cas} onValueChange={v => setHandicap(p => ({...p, condition_cas: v}))}>
              <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="oui">Oui</SelectItem>
                <SelectItem value="non">Non</SelectItem>
              </SelectContent>
            </Select>
            <div className="mt-2 text-xs text-[var(--oh-text-muted)] space-y-1 bg-[var(--oh-bg-elevated)] p-2 rounded">
              <p>• <strong>Cas n°1 :</strong> Moins de 80 ans + chute avec hospitalisation ET perte d'autonomie (6 mois) ET accompagnement aidant</p>
              <p>• <strong>Cas n°2 :</strong> 80 ans ou plus + chute avec hospitalisation ET perte d'autonomie (6 mois) OU accompagnement aidant</p>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== 3. BESOINS TERRAIN ===== */}
      <Section title="Besoins (terrain)" icon={User} id="besoins"
        open={openSections.besoins} onToggle={() => toggleSection("besoins")}
        badge={besoins.difficultes_quotidiennes ? <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] text-xs">Rempli</Badge> : <Badge className="bg-[var(--oh-error-bg)] text-[var(--oh-error-text)] text-xs">Requis</Badge>}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[var(--oh-text-secondary)] text-xs">Âge</Label>
            <Input value={besoins.age} onChange={e => setBesoins(p => ({...p, age: e.target.value}))} placeholder="ex: 78" className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm" data-testid="dal-age" />
          </div>
          <div>
            <Label className="text-[var(--oh-text-secondary)] text-xs">Mobilité</Label>
            <Select value={besoins.mobilite} onValueChange={v => setBesoins(p => ({...p, mobilite: v}))}>
              <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="autonome">Autonome</SelectItem>
                <SelectItem value="aide_technique">Aide technique</SelectItem>
                <SelectItem value="aide_humaine">Aide humaine</SelectItem>
                <SelectItem value="fauteuil">Fauteuil roulant</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[var(--oh-text-secondary)] text-xs">GIR</Label>
            <Select value={String(canonicalDisplay("gir", besoins.gir) || "non_evalue")}
              onValueChange={v => setBesoins(p => ({...p, gir: v}))}
              disabled={isLockedCanonical("gir")}>
              <SelectTrigger data-testid="dal-gir" className={`bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] h-8 text-sm ${isLockedCanonical("gir") ? "opacity-60 cursor-not-allowed" : ""}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="non_evalue">Non évalué</SelectItem>
                <SelectItem value="non_requis">Non requis (≥ 70 ans)</SelectItem>
                {[1,2,3,4,5,6].map(n => <SelectItem key={n} value={String(n)}>GIR {n}</SelectItem>)}
              </SelectContent>
            </Select>
            {isLockedCanonical("gir") && (
              <p className="text-[10px] text-[var(--oh-text-muted)] mt-0.5">{lockedHint}</p>
            )}
            {(() => {
              const ageNum = parseInt(besoins.age || beneficiaire.age || 0, 10);
              if (ageNum >= 70 && (!besoins.gir || besoins.gir === "non_evalue")) {
                return <p className="text-[10px] text-[var(--oh-info-text)] mt-1">Bénéficiaire ≥ 70 ans : GIR non requis — sélectionne "Non requis" si applicable.</p>;
              }
              return null;
            })()}
          </div>
          <div className="flex items-center gap-4 pt-4">
            <label
              className={`flex items-center gap-1.5 text-xs text-[var(--oh-text-secondary)] ${isLockedCanonical("apa") ? "opacity-60 cursor-not-allowed" : ""}`}
              title={isLockedCanonical("apa") ? lockedHint : undefined}
            >
              <input type="checkbox"
                checked={Boolean(canonicalDisplay("apa", besoins.apa))}
                onChange={e => setBesoins(p => ({...p, apa: e.target.checked}))}
                disabled={isLockedCanonical("apa")}
                data-testid="dal-apa"
                className="rounded" /> APA
            </label>
            <label className="flex items-center gap-1.5 text-xs text-[var(--oh-text-secondary)]">
              <input type="checkbox" checked={besoins.rqth} onChange={e => setBesoins(p => ({...p, rqth: e.target.checked}))} className="rounded" /> RQTH
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[var(--oh-text-secondary)] text-xs">Historique (chutes, incidents)</Label>
              <AITranscribeButton value={besoins.historique_chutes} onResult={v => setBesoins(p => ({...p, historique_chutes: v}))} context="historique de chutes et incidents du bénéficiaire MaPrimeAdapt" />
            </div>
            <Textarea value={besoins.historique_chutes} onChange={e => setBesoins(p => ({...p, historique_chutes: e.target.value}))} placeholder="Ex: 2 chutes en 6 mois..." className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] text-sm min-h-[60px]" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[var(--oh-text-secondary)] text-xs">Usage réel du logement</Label>
              <AITranscribeButton value={besoins.usage_reel} onResult={v => setBesoins(p => ({...p, usage_reel: v}))} context="usage réel du logement par le bénéficiaire" />
            </div>
            <Textarea value={besoins.usage_reel} onChange={e => setBesoins(p => ({...p, usage_reel: e.target.value}))} placeholder="Ex: vit seul, ne monte plus à l'étage..." className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] text-sm min-h-[60px]" />
          </div>
        </div>
        <div className="mt-3">
          <div className="flex items-center justify-between">
            <Label className="text-[var(--oh-error-text)] text-xs font-medium">Description concrète des difficultés quotidiennes *</Label>
            <AITranscribeButton value={besoins.difficultes_quotidiennes} onResult={v => setBesoins(p => ({...p, difficultes_quotidiennes: v}))} context="difficultés quotidiennes du bénéficiaire âgé dans son logement pour dossier MaPrimeAdapt ANAH" />
          </div>
          <Textarea value={besoins.difficultes_quotidiennes} onChange={e => setBesoins(p => ({...p, difficultes_quotidiennes: e.target.value}))}
            placeholder="Ex: difficulté à enjamber la baignoire, besoin d'appui pour se lever..."
            className="bg-[var(--oh-bg-surface)] border-[var(--oh-error-border)] text-[var(--oh-text)] text-sm min-h-[80px] mt-1" data-testid="dal-difficultes" />
          <p className="text-xs text-[var(--oh-error-text)] mt-1">Champ obligatoire — clé pour la solidité du dossier ANAH</p>
        </div>
      </Section>

      {/* ===== 4. ÉTAT DES LIEUX ET PRÉCONISATIONS ===== */}
      <Section title="État des Lieux et Préconisations" icon={Home} id="zones"
        open={openSections.zones} onToggle={() => toggleSection("zones")}
        badge={<Badge className="text-xs" style={neutralBadgeStyle}>{zones.filter(z => z.etat === "travaux_necessaires").length} zone(s) à traiter</Badge>}>
        <div className="space-y-3">
          {/* Bouton génération 3D globale */}
          {zones.filter(z => z.etat === "travaux_necessaires" && z.preconisations?.length > 0).length > 1 && (
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={generateAllZones3D}
                disabled={generatingAllZones3d}
                className="text-xs h-7 bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]"
                data-testid="btn-générer-toutes-3d"
              >
                {generatingAllZones3d
                  ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Génération en cours…</>
                  : <><Sparkles className="w-3 h-3 mr-1" />Générer toutes les vues 3D</>
                }
              </Button>
            </div>
          )}
          {zones.map((zone, idx) => {
            const needsWork = zone.etat === "travaux_necessaires";
            const has3d = !!zone3dImages[zone.id];
            const isGenerating3d = !!generatingZone3d[zone.id];
            return (
              <div key={zone.id} className={`rounded-lg border ${needsWork ? 'border-[var(--oh-error-border)] bg-[var(--oh-error-bg)]' : 'border-[var(--oh-border)] bg-[var(--oh-bg-elevated)]'}`} data-testid={`dal-zone-${zone.id}`}>
                {/* Zone header */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3">
                  <span className="text-[var(--oh-text)] text-sm font-medium min-w-[180px]">{zone.label}</span>
                  <div className="flex items-center gap-2 flex-wrap flex-1">
                    <Select value={zone.etat} onValueChange={v => { const nz = [...zones]; nz[idx].etat = v; nz[idx].adapte = v !== "travaux_necessaires"; setZones(nz); }}>
                      <SelectTrigger className={`h-7 text-xs w-[200px] ${needsWork ? 'bg-[var(--oh-error-bg)] border-[var(--oh-error-border)] text-[var(--oh-error-text)]' : 'bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text-secondary)]'}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ETAT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <label className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={!zone.adapte} onChange={e => { const nz = [...zones]; nz[idx].adapte = !e.target.checked; setZones(nz); }} className="rounded" />
                      <span className={zone.adapte ? "text-[var(--oh-success-text)]" : "text-[var(--oh-error-text)]"}>{zone.adapte ? "Adapté" : "Non adapté"}</span>
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={zone.eligible_subventions || false} onChange={e => { const nz = [...zones]; nz[idx].eligible_subventions = e.target.checked; setZones(nz); }} className="rounded" />
                      <span className="text-[var(--oh-info-text)]">Éligible subv.</span>
                    </label>
                  </div>
                </div>
                {/* Zone details when work needed */}
                {needsWork && (
                  <div className="px-3 pb-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <div className="flex items-center justify-between">
                          <Label className="text-[var(--oh-error-text)] text-xs font-medium">Problématiques</Label>
                          <AITranscribeButton value={(zone.problematiques || []).join("\n")}
                            onResult={v => { const nz = [...zones]; nz[idx].problematiques = v.split("\n").filter(Boolean); setZones(nz); }}
                            context={`problématiques de la zone "${zone.label}" dans un logement pour diagnostic autonomie`} />
                        </div>
                        <Textarea value={(zone.problematiques || []).join("\n")}
                          onChange={e => { const nz = [...zones]; nz[idx].problematiques = e.target.value.split("\n").filter(Boolean); setZones(nz); }}
                          placeholder="Une par ligne" className="bg-[var(--oh-error-bg)] border-[var(--oh-error-border)] text-[var(--oh-text)] text-xs min-h-[60px]" />
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <Label className="text-[var(--oh-success-text)] text-xs font-medium">Préconisations</Label>
                          <AITranscribeButton value={(zone.preconisations || []).join("\n")}
                            onResult={v => { const nz = [...zones]; nz[idx].preconisations = v.split("\n").filter(Boolean); setZones(nz); }}
                            context={`préconisations de travaux pour la zone "${zone.label}" pour adaptation du logement MaPrimeAdapt`} />
                        </div>
                        <Textarea value={(zone.preconisations || []).join("\n")}
                          onChange={e => { const nz = [...zones]; nz[idx].preconisations = e.target.value.split("\n").filter(Boolean); setZones(nz); }}
                          placeholder="Une par ligne" className="bg-[var(--oh-success-bg)] border-[var(--oh-success-border)] text-[var(--oh-text)] text-xs min-h-[60px]" />
                      </div>
                    </div>
                    {/* Génération 3D par zone */}
                    {zone.preconisations?.length > 0 && (
                      <div className="mt-2 flex items-center gap-3">
                        <Button
                          size="sm"
                          onClick={() => generateZone3D(zone)}
                          disabled={isGenerating3d}
                          className="text-xs h-7 bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]"
                          data-testid={`btn-générer-3d-${zone.id}`}
                        >
                          {isGenerating3d
                            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Génération…</>
                            : has3d
                              ? <><Sparkles className="w-3 h-3 mr-1" />Régénérer 3D</>
                              : <><Sparkles className="w-3 h-3 mr-1" />Générer vue 3D</>
                          }
                        </Button>
                        {has3d && !isGenerating3d && (
                          <div className="flex items-center gap-2">
                            <img
                              src={zone3dImages[zone.id].image_base64
                                ? `data:image/png;base64,${zone3dImages[zone.id].image_base64}`
                                : `${API}${zone3dImages[zone.id].url}`}
                              alt="Vue 3D"
                              className="h-14 w-20 object-cover rounded border border-[var(--oh-accent)]"
                            />
                            <span className="text-[var(--oh-accent-text)] text-xs">Vue 3D générée — incluse dans le PDF</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* ===== 5. SOLUTIONS MAPPING ===== */}
      <Section title="Solutions (mapping 1:1)" icon={Target} id="solutions"
        open={openSections.solutions} onToggle={() => toggleSection("solutions")}
        badge={
          <div className="flex items-center gap-2">
            <Badge className="text-xs" style={neutralBadgeStyle}>{solutions.length} mapping(s)</Badge>
            <Button
              size="sm" variant="outline"
              onClick={(e) => { e.stopPropagation(); regenerateMapping(); }}
              disabled={generatingPipeline || zones.filter(z => z.etat === "travaux_necessaires").length === 0}
              className="h-6 px-2 text-[11px]"
              data-testid="dal-regenerate-mapping"
              title="Relance l'IA et reconstruit le mapping depuis les zones"
            >
              {generatingPipeline ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Wand2 className="w-3 h-3 mr-1" />}
              Régénérer
            </Button>
          </div>
        }>
        <div className="space-y-2">
          {solutions.map((s, idx) => {
            const suggestions = suggestActiveIdx === idx ? computeSuggestions(s.probleme) : [];
            const showSuggest = suggestActiveIdx === idx && suggestions.length > 0;
            return (
            <div key={idx} className="flex gap-2 items-start">
              <div className="flex-1 relative">
                <Input
                  value={s.probleme}
                  onChange={e => {
                    const ns = [...solutions]; ns[idx].probleme = e.target.value; setSolutions(ns);
                  }}
                  onFocus={() => setSuggestActiveIdx(idx)}
                  onBlur={() => setTimeout(() => setSuggestActiveIdx(null), 150)}
                  placeholder="Problème identifié"
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] text-xs h-7"
                  data-testid={`dal-mapping-probleme-${idx}`}
                />
                {showSuggest && (
                  <div
                    className="absolute z-30 left-0 right-0 mt-1 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-md shadow-lg max-h-64 overflow-y-auto"
                    data-testid={`dal-mapping-suggestions-${idx}`}
                  >
                    {suggestions.map((m, i) => (
                      <button
                        key={i}
                        type="button"
                        onMouseDown={(e) => {
                          // mouseDown plutôt que onClick : précède le blur de l'Input
                          e.preventDefault();
                          const ns = [...solutions];
                          ns[idx] = {
                            ...ns[idx],
                            probleme: m.probleme,
                            solution: m.solutions?.[0] || ns[idx].solution || "",
                          };
                          setSolutions(ns);
                          setSuggestActiveIdx(null);
                        }}
                        className="w-full text-left px-2 py-1.5 hover:bg-[var(--oh-bg-elevated)] border-b border-[var(--oh-border)] last:border-b-0"
                      >
                        <div className="text-xs text-[var(--oh-text)]">{m.probleme}</div>
                        <div className="text-[10px] text-[var(--oh-success-text)] truncate">→ {m.solutions?.[0]}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className="text-[var(--oh-text-secondary)] pt-1 text-xs">→</span>
              <div className="flex-1">
                <Input value={s.solution} onChange={e => { const ns = [...solutions]; ns[idx].solution = e.target.value; setSolutions(ns); }}
                  placeholder="Solution proposée"
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] text-xs h-7"
                  data-testid={`dal-mapping-solution-${idx}`} />
              </div>
              <Button size="sm" variant="ghost" onClick={() => setSolutions(solutions.filter((_, i) => i !== idx))} className="text-[var(--oh-error-text)] h-7 w-7 p-0"><X className="w-3 h-3" /></Button>
            </div>
            );
          })}
          <Button size="sm" variant="outline" onClick={() => setSolutions([...solutions, { probleme: "", solution: "", zone: "" }])} className="text-xs">
            <Plus className="w-3 h-3 mr-1" /> Ajouter
          </Button>
          {zones.filter(z => z.etat === "travaux_necessaires").length > 0 && solutions.length === 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="text-[var(--oh-accent-text)] text-xs"
              onClick={() => setSolutions(buildSolutionsMappingFromZones(zones))}
              data-testid="dal-auto-fill-solutions"
            >
              <Wand2 className="w-3 h-3 mr-1" /> Auto-remplir depuis zones
            </Button>
          )}
        </div>
      </Section>

      {/* ===== 6-bis. BLOC 4 · LE PLAN, ET SA VALIDATION =====
           🔑 L'analyse IA ci-dessous RESTE en lecture seule : elle décrit le
           dossier. Le PLAN, lui, se décide — proposition par proposition — et
           c'est lui que la génération exigera validé. Les deux ne se confondent
           pas : l'un explique, l'autre engage. */}
      <PlanDalPanel dossierId={dossierId} tenantId={tenantId} onPlanChange={onUpdate} />

      {/* ===== 6. ANALYSE IA ===== */}
      <Section title="Analyse IA (Risque, Justification, Cohérence)" icon={Zap} id="pipeline"
        open={openSections.pipeline} onToggle={() => toggleSection("pipeline")}
        badge={pipeline ? <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] text-xs">Généré</Badge> : null}>
        {!pipeline ? (
          <div className="text-center py-6">
            <p className="text-[var(--oh-text-secondary)] text-sm mb-3">Remplissez les sections, puis générez l'analyse IA.</p>
            <Button onClick={generatePipeline} disabled={generatingPipeline} variant="outline" style={btnSecondary}>
              {generatingPipeline ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
              Générer l'analyse IA
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {(pipeline.risques || []).length > 0 && (
              <div>
                <h4 className="text-[var(--oh-info-text)] font-medium text-sm mb-2"><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />Risques</h4>
                {pipeline.risques.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-[var(--oh-bg-surface)] rounded text-xs mb-1">
                    <Badge className="text-xs" style={RISK_STYLE[r.niveau] || RISK_STYLE.faible}>{r.niveau}</Badge>
                    <span className="text-[var(--oh-text-secondary)] font-medium">{r.zone}</span> — <span className="text-[var(--oh-text)]">{r.risque}</span>
                  </div>
                ))}
              </div>
            )}
            {(pipeline.justifications || []).length > 0 && (
              <div>
                <h4 className="text-[var(--oh-accent-text)] font-medium text-sm mb-2"><ShieldCheck className="w-3.5 h-3.5 inline mr-1" />Justifications</h4>
                {pipeline.justifications.map((j, i) => (
                  <div key={i} className="p-2 bg-[var(--oh-bg-surface)] rounded text-xs mb-1">
                    <p className="text-[var(--oh-accent-text)] font-medium">{j.solution}</p>
                    <p className="text-[var(--oh-text-secondary)] italic">{j.justification}</p>
                  </div>
                ))}
              </div>
            )}
            {pipeline.coherence && (
              <div>
                <h4 className="text-[var(--oh-success-text)] font-medium text-sm mb-2"><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Cohérence</h4>
                {Object.entries(pipeline.coherence).map(([k, v]) => (
                  <p key={k} className="text-[var(--oh-text-secondary)] text-xs p-1.5 bg-[var(--oh-bg-surface)] rounded mb-1"><span className="text-[var(--oh-success-text)] font-medium capitalize">{k} :</span> {v}</p>
                ))}
              </div>
            )}
            {pipeline.economique && (
              <div>
                <h4 className="text-[var(--oh-success-text)] font-medium text-sm mb-2"><Scale className="w-3.5 h-3.5 inline mr-1" />Cadrage économique</h4>
                {Object.entries(pipeline.economique).map(([k, v]) => (
                  <p key={k} className="text-[var(--oh-text-secondary)] text-xs p-1.5 bg-[var(--oh-bg-surface)] rounded mb-1"><span className="text-[var(--oh-success-text)] font-medium capitalize">{k.replace(/_/g, ' ')} :</span> {v}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </Section>

      {/* ===== 7. PHOTOS ===== */}
      <Section title="Photographies" icon={Camera} id="photos"
        open={openSections.photos} onToggle={() => toggleSection("photos")}
        badge={<Badge className="text-xs" style={neutralBadgeStyle}>{photos.length} photo(s)</Badge>}>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((p, i) => (
            <div key={i} className="relative group">
              <img src={`${API}${p.url || p.filepath}`} alt={p.zone || "photo"} className="w-full h-24 object-cover rounded border border-[var(--oh-border)]" />
              <Badge className="absolute bottom-1 left-1 bg-[var(--oh-bg)] text-xs">{p.zone}</Badge>
            </div>
          ))}
          <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-[var(--oh-border)] rounded cursor-pointer hover:border-[var(--oh-accent)] transition-colors">
            {uploading ? <Loader2 className="w-5 h-5 animate-spin text-[var(--oh-accent-text)]" /> : <Upload className="w-5 h-5 text-[var(--oh-text-secondary)]" />}
            <span className="text-[var(--oh-text-secondary)] text-xs mt-1">Ajouter</span>
            <input type="file" multiple accept="image/*" onChange={handlePhotoUpload} className="hidden" />
          </label>
        </div>
      </Section>

      {/* ===== 8. CROQUIS / PLAN 2D ===== */}
      <Section title="Croquis Travaux / Plan 2D" icon={Grid} id="plan2d"
        open={openSections.plan2d} onToggle={() => toggleSection("plan2d")}>
        <ImportPlan2DBlock dossierId={dossierId} tenantId={tenantId} onImported={loadDAL} />
        <Plan2DAutoBlock
          dossierId={dossierId}
          tenantId={tenantId}
          photos={photos}
          zones={zones}
          onAutoReady={(planData) => {
            setPlan2DInitialData(planData);
            toast.success(`Plan IA généré — ${planData.equipments?.length || 0} équipements détectés`);
          }}
        />
        <Plan2DGenerator
          key={plan2DInitialData ? `ia-${plan2DInitialData.dims?.longueur_cm}-${plan2DInitialData.dims?.largeur_cm}` : "manual"}
          dossierId={dossierId}
          tenantId={tenantId}
          initialData={plan2DInitialData}
          roomData={(() => {
            const sdb = zones.find(z => z.id === "salle_de_bain") || {};
            const f = sdb.fields || {};
            const w = parseFloat(f.largeur);
            const h = parseFloat(f.longueur);
            return {
              width: Number.isFinite(w) ? w : undefined,
              height: Number.isFinite(h) ? h : undefined,
              name: "Salle de bain",
            };
          })()}
          onSave={async (data) => {
            // Fix 2026-05-20 : Plan2DGenerator construit TOUJOURS plan_2d_data
            // (mode manuel inclus). L'early-return `if (!payload.dims) return`
            // historique skippait le save en mode manuel → dimensions
            // 246×160 réapparaissaient au reload (bug constaté JJ : 350×220
            // saisi → 246×160 affiché après reload).
            const payload = data.plan_2d_data;
            if (!payload || !payload.dims) {
              toast.error("Plan 2D : données invalides (dims manquantes)");
              return;
            }
            try {
              const token = localStorage.getItem("session_token");
              const res = await fetch(
                `${API}/api/metier/tef/dossiers/${dossierId}/plan-2d/save`,
                { credentials: "include",
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    "X-Tenant-ID": tenantId || "tef",
                    ...(token && { Authorization: `Bearer ${token}` }),
                  },
                  credentials: "include",
                  body: JSON.stringify(payload),
                }
              );
              if (res.ok) {
                toast.success("Plan 2D sauvegardé");
              } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || "Erreur sauvegarde plan 2D");
              }
            } catch (e) {
              toast.error(`Erreur réseau : ${e.message}`);
            }
          }}
        />
      </Section>

      {/* ===== 9. PROJECTION APRÈS (IA) ===== */}
      <Section title="Projection APRÈS (IA)" icon={Sparkles} id="aiAfter"
        open={openSections.aiAfter} onToggle={() => toggleSection("aiAfter")}>
        <AIImageAfter dossierId={dossierId} tenantId={tenantId} photos={photos} />
      </Section>

      {/* ===== INTERVENANT ===== */}
      <div className="p-3 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg">
        <h4 className="text-[var(--oh-text-secondary)] text-xs font-medium mb-2">Intervenant & Observations</h4>
        {/* Dropdown visiteurs */}
        {visiteurs.length > 0 && (
          <select
            value={intervenant.nom && intervenant.prenom ? `${intervenant.nom}|${intervenant.prenom}|${intervenant.fonction || ""}` : ""}
            onChange={e => {
              if (e.target.value === "__new__") { setIntervenant({ nom: "", prenom: "", fonction: "" }); }
              else if (e.target.value) { const [n, p, f] = e.target.value.split("|"); setIntervenant({ nom: n, prenom: p || "", fonction: f || "" }); }
            }}
            className="w-full h-8 px-2 rounded-md bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] text-[var(--oh-text)] text-xs mb-2"
            data-testid="dal-intervenant-select"
          >
            <option value="">Sélectionner un visiteur</option>
            {visiteurs.map(v => <option key={v.id} value={`${v.nom}|${v.prenom || ""}|${v.type || ""}`}>{v.nom} {v.prenom || ""} — {v.type || "AMO"}</option>)}
            <option value="__new__">+ Saisir manuellement</option>
          </select>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          <Input value={intervenant.nom} onChange={e => setIntervenant(p => ({...p, nom: e.target.value}))} placeholder="Nom" className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] text-xs h-7" />
          <Input value={intervenant.prenom} onChange={e => setIntervenant(p => ({...p, prenom: e.target.value}))} placeholder="Prénom" className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] text-xs h-7" />
          <Input value={intervenant.fonction} onChange={e => setIntervenant(p => ({...p, fonction: e.target.value}))} placeholder="Fonction" className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] text-xs h-7" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[var(--oh-text-secondary)] text-xs">Observations générales</span>
            <AITranscribeButton value={observations} onResult={v => setObservations(v)} context="observations générales du diagnostic autonomie logement" />
          </div>
          <Textarea value={observations} onChange={e => setObservations(e.target.value)} placeholder="Observations générales..."
            className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] text-xs min-h-[50px]" />
        </div>
      </div>

      {/* ===== HISTORIQUE DES MODIFICATIONS ===== */}
      <div className="p-3 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-[var(--oh-text-secondary)] text-xs font-medium flex items-center gap-1.5"><HistoryIcon className="w-3.5 h-3.5 text-[var(--oh-info-text)]" /> Historique des modifications</h4>
          <Button variant="link" size="sm" onClick={loadDalHistory} className="h-auto p-0 text-[10px]" style={{ color: "var(--oh-accent-text)" }} data-testid="dal-history-refresh">Actualiser</Button>
        </div>
        {dalHistory.length === 0 ? (
          <p className="text-[var(--oh-text-muted)] text-xs italic">Aucune modification enregistrée</p>
        ) : (
          <div className="max-h-[200px] overflow-y-auto space-y-1.5">
            {dalHistory.map((h, i) => (
              <div key={h.id || i} className="flex items-start gap-2 text-xs p-1.5 rounded bg-[var(--oh-bg-surface)]" data-testid={`dal-history-entry-${i}`}>
                <span className="text-[var(--oh-info-text)] font-mono whitespace-nowrap">{new Date(h.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                <span className="text-[var(--oh-text-secondary)]">—</span>
                <span className="text-[var(--oh-text)]">{h.utilisateur_nom || h.utilisateur_id || "Système"}</span>
                <span className="text-[var(--oh-text-secondary)]">:</span>
                <span className="text-[var(--oh-text-secondary)] flex-1">{h.description}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
